import { GetReturnErrorCode } from "@redotech/customer-portal-sdk/rpc/schema/get-return-enums";
import { UseCreationStatusResult } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-creation-status";
import { ErrorCodeInfo } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-return-confirmation";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { Card } from "../../submission/components/card";
import { ModallessPageLayout } from "../../submission/components/modalless-page-layout";
import { CreationStatus } from "../components/creation-status";
import { MaybeDisplayHtml } from "../components/maybe-display-html";
import { Spinner } from "../spinner";
import {
  ConfirmationStatusSection,
  ConfirmationStatusSectionProps,
} from "./status-section";
import {
  ConfirmationSummarySection,
  ConfirmationSummarySectionProps,
} from "./summary-section";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export type {
  ReturnConfirmationDisplayManualLabelData,
  ReturnConfirmationDisplayManualLabelInfo,
  ReturnConfirmationDisplayReturnHeader,
} from "./types";

export interface ReturnConfirmationDisplayProps
  extends ConfirmationStatusSectionProps, ConfirmationSummarySectionProps {
  loadingReturn: boolean;
  error: string | undefined;
  errorCodeData: ErrorCodeInfo | undefined;

  showCreationStatus: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  returnType: ReturnTypeEnum;
  hasCreationStatusError: boolean;

  rejected: boolean;
  rejectHtml: string;
  texts: { rejectedText: string; returnItems: string; newItems: string };

  shouldShowSummary: boolean;
  modalless?: boolean;
}

export function ReturnConfirmationDisplay(
  props: ReturnConfirmationDisplayProps,
) {
  const {
    loadingReturn,
    error,
    errorCodeData,
    showCreationStatus,
    creationStatus,
    returnType,
    hasCreationStatusError,
    rejected,
    rejectHtml,
    texts,
    shouldShowSummary,
    modalless,
  } = props;

  if (errorCodeData) {
    switch (errorCodeData.type) {
      case GetReturnErrorCode.ReturnNotFound:
        return (
          <Flex flexDirection="column" gap="sm" p="md">
            <Text fontWeight="bold" textAlign="center">
              {errorCodeData?.data.texts.title}
            </Text>
          </Flex>
        );
      default:
        assertNever(errorCodeData.type);
    }
  }
  if (error) {
    return <Text>{error}</Text>;
  }
  if (showCreationStatus) {
    return (
      <CreationStatus
        creationStatus={creationStatus}
        hasCreationStatusError={hasCreationStatusError}
        returnType={returnType}
        showCreationStatus={showCreationStatus}
      />
    );
  }

  if (loadingReturn) {
    const loadingContent = (
      <Flex
        align="center"
        data-ui="loading"
        flexDirection="column"
        grow={1}
        justify="center"
        pb="6xl"
      >
        <Spinner />
      </Flex>
    );
    if (modalless) {
      return (
        <ModallessPageLayout uiSelector="return-confirmation">
          {loadingContent}
        </ModallessPageLayout>
      );
    }
    return (
      <Card fullscreen uiSelector="return-confirmation">
        {loadingContent}
      </Card>
    );
  }

  if (rejected) {
    return (
      <Flex data-ui="rejected" flexDirection="column" gap="sm" p="md">
        <Text fontWeight="bold" textAlign="center">
          {texts.rejectedText}
        </Text>
        <MaybeDisplayHtml html={rejectHtml} />
      </Flex>
    );
  }

  const confirmationBody = (
    <Flex flexDirection="column" gap="2xl" grow={1} pb="4xl">
      <ConfirmationStatusSection
        customSuccessMessage={props.customSuccessMessage}
        deadlineDays={props.deadlineDays}
        manualLabelData={props.manualLabelData}
        manualLabelInfo={props.manualLabelInfo}
        modalless={modalless}
        onCancelClick={props.onCancelClick}
        onOpenUrl={props.onOpenUrl}
        pickupModals={props.pickupModals}
        returnHeader={props.returnHeader}
        returnId={props.returnId}
        searchBlueYonderLocations={props.searchBlueYonderLocations}
        searchCarrierLocations={props.searchCarrierLocations}
        searchDropoffLocations={props.searchDropoffLocations}
        searchInStoreLocations={props.searchInStoreLocations}
        shouldAutoOpenBoxlessLocations={props.shouldAutoOpenBoxlessLocations}
        showCancelButton={props.showCancelButton}
        showDeadline={props.showDeadline}
        showReturnSteps={props.showReturnSteps}
        stepResource={props.stepResource}
        steps={props.steps}
        warnings={props.warnings}
      />
      {shouldShowSummary && (
        <ConfirmationSummarySection
          currency={props.currency}
          hideItemCosts={props.hideItemCosts}
          isConvenienceFee={props.isConvenienceFee}
          isWarranty={returnType === ReturnTypeEnum.WARRANTY}
          labelDeducted={props.labelDeducted}
          modalless={modalless}
          newItems={props.newItems}
          newItemsHeader={props.newItemsHeader}
          newOrderAddress={props.newOrderAddress}
          onEditAddressClick={props.onEditAddressClick}
          outstandingBalanceManuallyWaived={
            props.outstandingBalanceManuallyWaived
          }
          products={props.products}
          returnAddress={props.returnAddress}
          returnItemsHeader={props.returnItemsHeader}
          returnTotals={props.returnTotals}
          settings={props.settings}
          showAddresses={props.showAddresses}
          showEditAddressButton={props.showEditAddressButton}
          summaryFeesHeader={props.summaryFeesHeader}
          summaryFeesOrderHeader={props.summaryFeesOrderHeader}
          upsellBillingTrigger={props.upsellBillingTrigger}
        />
      )}
    </Flex>
  );

  if (modalless) {
    return (
      <ModallessPageLayout uiSelector="return-confirmation">
        {confirmationBody}
      </ModallessPageLayout>
    );
  }

  return (
    <Card fullscreen uiSelector="return-confirmation">
      {confirmationBody}
    </Card>
  );
}
